<template>
  <div class="system-process-page">
    <div class="process-tabs" role="tablist" aria-label="ติดตามการประมวลผล">
      <button
        type="button"
        role="tab"
        :aria-selected="activeTab === 'LATEST' ? 'true' : 'false'"
        :class="{ active: activeTab === 'LATEST' }"
        @click="activeTab = 'LATEST'"
      >
        สถานะการประมวลผลล่าสุด
      </button>
      <button
        type="button"
        role="tab"
        :aria-selected="activeTab === 'CONTRACTS' ? 'true' : 'false'"
        :class="{ active: activeTab === 'CONTRACTS' }"
        @click="activeTab = 'CONTRACTS'"
      >
        รายการสัญญาและสถานะการประมวลผล
      </button>
      <button
        type="button"
        role="tab"
        :aria-selected="activeTab === 'HISTORY' ? 'true' : 'false'"
        :class="{ active: activeTab === 'HISTORY' }"
        @click="selectHistoryTab"
      >
        ประวัติการคำนวณดอกเบี้ยรายวัน
      </button>
    </div>
    <vx-card v-show="activeTab === 'LATEST'" class="mb-6">
      <div class="process-card-heading">
        <div>
          <span class="process-eyebrow">สถานะงานล่าสุด</span>
          <h3>สถานะการประมวลผลล่าสุด</h3>
        </div>
        <button
          type="button"
          class="process-icon-button"
          title="รีเฟรชสถานะล่าสุด"
          aria-label="รีเฟรชสถานะล่าสุด"
          :disabled="latestLoading"
          @click="loadLatest"
        >
          <feather-icon icon="RotateCwIcon" />
        </button>
      </div>
      <p v-if="latestError" role="alert" class="text-danger mb-4">
        {{ latestError }}
      </p>
      <div v-if="latestRun" class="latest-run" role="status">
        <div class="latest-run-title">
          <div>
            <strong>
              {{ latestRunHeading(latestRun) }}
            </strong>
            <div v-if="!latestRun.isRunning" class="process-meta">
              แสดงผลการประมวลผลครั้งล่าสุด
            </div>
            <code class="process-run latest-run-id">Run ID: {{ latestRun.id }}</code>
            <div class="process-meta">
              แหล่งที่มา: {{ calculationSource(latestRun.source) }}
            </div>
          </div>
          <div class="latest-run-state">
            <span
              class="process-badge"
              :class="
                latestRun.isRunning
                  ? 'process-badge-active'
                  : latestRun.endAt
                  ? 'process-badge-idle'
                  : 'process-badge-stuck'
              "
            >
              {{ latestRun.isRunning ? "กำลังทำงาน" : latestRun.endAt ? "ไม่มีงานกำลังทำ" : "อาจค้าง" }}
            </span>
            <span v-if="!latestRun.isRunning" class="process-meta">
              ผลรอบล่าสุด: {{ dailyRunStatus(latestRun.status) }}
            </span>
          </div>
        </div>
        <div class="process-overview latest-overview">
          <div class="process-stat process-stat-pending">
            <span>ทั้งหมด</span>
            <strong>{{ number(latestRun.totalContracts) }}</strong>
            <small>สัญญาในรอบงานนี้</small>
          </div>
          <div class="process-stat process-stat-running">
            <span>ประมวลผลแล้ว</span>
            <strong>{{ number(latestRun.processedContracts) }}</strong>
            <small>รวมสำเร็จและไม่สำเร็จ</small>
          </div>
          <div class="process-stat process-stat-success">
            <span>สำเร็จ</span>
            <strong>{{ number(latestRun.completedContracts) }}</strong>
            <small>transaction จบสมบูรณ์</small>
          </div>
          <div class="process-stat process-stat-stuck">
            <span>ไม่สำเร็จ</span>
            <strong>{{ number(latestRun.failedContracts) }}</strong>
            <small>ข้ามและทำรายการถัดไป</small>
          </div>
        </div>
        <div class="latest-progress-label">
          <span>
            {{ latestRun.isRunning ? "กำลังประมวลผล" : "รอบล่าสุดประมวลผล" }}
            {{ number(latestRun.processedContracts) }} จาก
            {{ number(latestRun.totalContracts) }} สัญญา
          </span>
          <span>{{ latestProgressPercent }}%</span>
        </div>
        <progress
          v-if="latestRun.totalContracts"
          :value="latestRun.processedContracts"
          :max="latestRun.totalContracts"
          aria-label="ความคืบหน้าการประมวลผลล่าสุด"
        ></progress>
        <div v-if="latestRun.isRunning && latestRun.current" class="current-progress">
          <div>
            <strong>สัญญาปัจจุบันลำดับที่ {{ number(latestRun.current.contractIndex) }}</strong>
            <code>{{ latestRun.current.contractId || "ไม่พบรหัสสัญญา" }}</code>
          </div>
          <div class="current-progress-detail">
            คำนวณภายในสัญญา {{ number(latestRun.current.completedDates) }} จาก
            {{ number(latestRun.current.totalDates) }} รายการ
            <span v-if="latestRun.current.currentDate">
              · วันที่ {{ latestRun.current.currentDate }}
            </span>
          </div>
          <progress
            v-if="latestRun.current.totalDates"
            :value="latestRun.current.completedDates"
            :max="latestRun.current.totalDates"
            aria-label="ความคืบหน้าภายในสัญญาปัจจุบัน"
          ></progress>
        </div>
        <div v-if="latestRun.isRunning" class="latest-run-meta">
          <span>เริ่ม {{ formatDate(latestRun.startAt) }}</span>
          <span>ทำงานมา {{ formatDuration(latestRun.elapsedSeconds) }}</span>
          <span>อัปเดตล่าสุด {{ formatDate(latestRun.progressUpdatedAt) }}</span>
        </div>
        <div v-else-if="latestRun.endAt" class="latest-run-meta">
          <span>รอบล่าสุดเริ่ม {{ formatDate(latestRun.startAt) }}</span>
          <span>สิ้นสุด {{ formatDate(latestRun.endAt) }}</span>
          <span>ใช้เวลารวม {{ formatDuration(latestRun.elapsedSeconds) }}</span>
        </div>
        <div v-else class="latest-run-meta">
          <span>รอบล่าสุดเริ่ม {{ formatDate(latestRun.startAt) }}</span>
          <span>ยังไม่มีเวลาสิ้นสุด</span>
          <span>อัปเดตล่าสุด {{ formatDate(latestRun.progressUpdatedAt) }}</span>
        </div>
      </div>
      <p v-else-if="latestLoading" aria-live="polite">กำลังโหลดสถานะล่าสุด…</p>
      <p v-else class="process-meta">ยังไม่พบประวัติการคำนวณดอกเบี้ยรายวัน</p>
    </vx-card>
    <vx-card v-show="activeTab === 'CONTRACTS'" class="mb-6">
      <form class="qr-filters" @submit.prevent="load(1)">
        <label
          >ค้นหาสัญญา<input
            v-model="search"
            maxlength="120"
            :disabled="busy"
            placeholder="เลขที่สัญญาหรือชื่อสัญญา"
            @input="scheduleSearch"
            @compositionstart="scheduleSearch({ isComposing: true })"
            @compositionend="scheduleSearch"
        /></label>
        <label
          >ประเภทงาน
          <select v-model="type" :disabled="busy" @change="load(1)">
            <option value="ALL">ทั้งหมด</option>
            <option value="PROCESSING">กำลังประมวลผล / อาจค้าง</option>
            <option value="PENDING_DAILY">รอคำนวณรายวัน</option>
          </select>
        </label>
        <button
          class="clear-filters-button"
          :disabled="busy"
          type="button"
          @click="clearFilters"
        >
          ล้างตัวกรอง
        </button>
      </form>
    </vx-card>
    <vx-card v-show="activeTab === 'CONTRACTS'">
      <div class="process-card-heading">
        <div aria-live="polite">
          <h3>รายการสัญญาและสถานะการประมวลผล</h3>
          <div class="process-meta">
            {{
              loading
                ? "กำลังโหลด…"
                : "ทั้งหมด " + total + " รายการ · ข้อมูลล่าสุด " + formatDate(checkedAt)
            }}
          </div>
        </div>
        <div class="process-actions">
          <button
            type="button"
            class="process-icon-button"
            title="รีเฟรชรายการสัญญา"
            aria-label="รีเฟรชรายการสัญญา"
            :disabled="busy"
            @click="refreshList"
          >
            <feather-icon icon="RotateCwIcon" />
          </button>
          <button
            v-if="canRelease"
            type="button"
            class="process-button danger"
            :disabled="busy"
            @click="prepareRelease()"
          >
            ปลดสถานะทั้งหมด
          </button>
        </div>
      </div>
      <p v-if="!logReadAvailable" class="process-notice process-notice-warning">
        อ่านรายละเอียดจากฐาน Log ไม่สำเร็จ แต่สถานะ process และเวลาเริ่มจากฐานหลักยังแสดงได้
      </p>
      <p class="process-notice">
        รวมงาน process = true และงานรอคำนวณประจำวันที่ {{ targetDate || "—" }}
        ตามเกณฑ์เดียวกับ Daily Calculators สถานะ “อาจค้าง” เป็นการประเมินจากเวลา
        ไม่ใช่หลักฐานให้ปลดสถานะหรือคำนวณซ้ำอัตโนมัติ
      </p>
      <p v-if="error" role="alert" class="text-danger mb-4">{{ error }}</p>
      <div class="qr-table-wrap" :aria-busy="loading ? 'true' : 'false'">
        <table class="qr-table">
          <thead>
            <tr>
              <th>สัญญา</th>
              <th>สถานะงาน</th>
              <th>รายละเอียดการคำนวณ</th>
              <th>เริ่มประมวลผล (เวลาไทย)</th>
              <th>ระยะเวลาที่ผ่านไป</th>
              <th>รหัสรอบงาน</th>
              <th>ตรวจสอบข้อมูล</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in items"
              :key="row.loanId"
              :class="{
                'process-selected': selected && selected.loanId === row.loanId,
              }"
            >
              <td>
                <strong>{{ row.contractNo }}</strong>
                <div class="process-name">{{ row.contractName }}</div>
              </td>
              <td>
                <span v-if="row.process" class="process-badge" :class="runtimeClass(row.runtimeStatus)">
                  {{ runtimeText(row.runtimeStatus) }}
                </span>
                <span v-else class="process-badge">ไม่ได้ประมวลผล</span>
                <span
                  v-if="row.pendingDaily"
                  class="process-badge process-badge-pending"
                  >รอคำนวณรายวัน</span
                >
                <div v-else class="process-meta">ไม่อยู่ในคิวคำนวณรายวัน</div>
              </td>
              <td>
                <template v-if="row.process">
                  <strong>{{ row.method || "ไม่พบรายละเอียดใน Log" }}</strong>
                  <div class="process-meta">
                    ประเภท: {{ row.calculationType || "—" }}
                  </div>
                  <div class="process-meta">
                    จำนวนงานย่อย:
                    {{ row.workCount === null ? "—" : row.workCount }}
                  </div>
                </template>
                <span v-else class="process-meta">ยังไม่ได้เริ่มประมวลผล</span>
              </td>
              <td>{{ row.process ? formatDate(row.startedAt) : "—" }}</td>
              <td>
                {{ row.process ? formatDuration(row.elapsedSeconds) : "—" }}
                <div v-if="row.runtimeStatus === 'CONFIRMING'" class="process-meta">
                  รอถึง {{ formatDate(row.staleAt) }}
                </div>
                <div v-if="row.runtimeStatus === 'POSSIBLY_STUCK'" class="process-meta text-danger">
                  เกินเกณฑ์ตั้งแต่ {{ formatDate(row.staleAt) }}
                </div>
              </td>
              <td>
                <code class="process-run">{{
                  row.process ? row.runId || "ไม่มีรหัสรอบงาน" : "—"
                }}</code>
              </td>
              <td>
                <button
                  v-if="row.process"
                  class="process-button"
                  size="small"
                  type="button"
                  :disabled="busy"
                  @click="inspect(row)"
                >
                  {{
                    inspecting && selected.loanId === row.loanId
                      ? "กำลังตรวจ…"
                      : "ตรวจการเปลี่ยนแปลง"
                  }}
                </button>
                <button
                  v-if="canRelease && row.process"
                  type="button"
                  class="process-button danger"
                  :disabled="busy"
                  @click="prepareRelease(row)"
                >
                  ปลดสถานะ
                </button>
                <span v-if="!row.process" class="process-meta">—</span>
              </td>
            </tr>
            <tr v-if="!items.length">
              <td colspan="7">
                {{
                  loading
                    ? "กำลังโหลด…"
                    : error
                    ? "ไม่สามารถโหลดรายการได้"
                    : "ไม่พบสัญญาตามเงื่อนไข"
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="qr-pagination">
        <label class="qr-page-size"
          >รายการต่อหน้า:
          <select
            :value="pageSize"
            :disabled="busy"
            @change="changeSize($event.target.value)"
          >
            <option
              v-for="size in [5, 10, 20, 25, 50, 100]"
              :key="size"
              :value="size"
            >
              {{ size }}
            </option>
          </select></label
        >
        <span
          >{{ items.length ? (page - 1) * pageSize + 1 : 0 }}–{{
            items.length ? (page - 1) * pageSize + items.length : 0
          }}
          จาก {{ total }}</span
        >
        <nav class="qr-page-buttons" aria-label="แบ่งหน้าสัญญา">
          <button
            type="button"
            title="หน้าแรก"
            aria-label="หน้าแรก"
            :disabled="busy || !!error || page <= 1"
            @click="load(1, appliedSearch, appliedType)"
          >
            «
          </button>
          <button
            type="button"
            title="ก่อนหน้า"
            aria-label="ก่อนหน้า"
            :disabled="busy || !!error || page <= 1"
            @click="load(page - 1, appliedSearch, appliedType)"
          >
            ‹
          </button>
          <span>{{ page }} / {{ pages }}</span>
          <button
            type="button"
            title="ถัดไป"
            aria-label="ถัดไป"
            :disabled="busy || !!error || page >= pages"
            @click="load(page + 1, appliedSearch, appliedType)"
          >
            ›
          </button>
          <button
            type="button"
            title="หน้าสุดท้าย"
            aria-label="หน้าสุดท้าย"
            :disabled="busy || !!error || page >= pages"
            @click="load(pages, appliedSearch, appliedType)"
          >
            »
          </button>
        </nav>
      </div>
    </vx-card>
    <vx-card
      v-if="activeTab === 'CONTRACTS' && (releaseResults.length || releaseError)"
      class="mt-6"
      title="ผลการปลดสถานะ"
    >
      <p v-if="releaseError" role="alert" class="text-danger">
        {{ releaseError }}
      </p>
      <div class="qr-table-wrap">
        <table class="qr-table">
          <thead>
            <tr>
              <th>สัญญา</th>
              <th>ผล</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="result in releaseResults" :key="result.loanId">
              <td>{{ result.contractNo }}</td>
              <td>{{ result.message }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </vx-card>
    <div v-if="activeTab === 'CONTRACTS' && releaseTargets.length" class="release-overlay">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="release-title"
        class="release-dialog"
      >
        <h3 id="release-title">
          ยืนยันปลดสถานะ {{ releaseTargets.length }} สัญญา
        </h3>
        <p>
          ปรับ process=false เฉพาะรายการที่ผ่านการตรวจ
          ไม่ย้อนข้อมูลและไม่คำนวณซ้ำ งานที่ยังไม่เริ่ม transaction
          อาจถูกยกเลิกการจอง ผู้ดูแลต้องตรวจสาเหตุของงานค้างแล้ว
        </p>
        <ul>
          <li v-for="row in releaseTargets" :key="row.loanId">
            {{ row.contractNo }} · {{ row.contractName }}
          </li>
        </ul>
        <div class="release-toolbar">
          <button
            class="process-button"
            type="button"
            :disabled="releasing"
            @click="releaseTargets = []"
          >
            ยกเลิก</button
          ><button
            class="process-button danger"
            type="button"
            :disabled="releasing"
            @click="confirmRelease"
          >
            {{ releasing ? "กำลังตรวจและปลดสถานะ…" : "ยืนยันปรับเป็น false" }}
          </button>
        </div>
      </section>
    </div>
    <vx-card v-show="activeTab === 'HISTORY'" class="mb-6">
      <form class="qr-filters" @submit.prevent="applyHistorySearch">
        <label
          >ค้นหา Run ID<input
            v-model="historySearch"
            maxlength="120"
            placeholder="รหัสรอบงาน"
            @input="scheduleHistorySearch"
            @compositionstart="scheduleHistorySearch({ isComposing: true })"
            @compositionend="scheduleHistorySearch"
        /></label>
        <label
          >แหล่งที่มา
          <select v-model="historySource" @change="loadHistory(1)">
            <option value="ALL">ทั้งหมด</option>
            <option value="CRON">งานอัตโนมัติ (Cron)</option>
            <option value="MANUAL">เรียก Mutation โดยผู้ดูแล</option>
            <option value="RECONCILIATION">คำนวณตามวันอ้างอิง (กระทบยอด)</option>
            <option value="LEGACY_UNKNOWN">ไม่ทราบ (ข้อมูลก่อนปรับปรุง)</option>
          </select>
        </label>
        <label
          >เวลาสิ้นสุด
          <select v-model="historyState" @change="loadHistory(1)">
            <option value="ALL">ทั้งหมด</option>
            <option value="OPEN">ยังไม่สิ้นสุด</option>
            <option value="CLOSED">สิ้นสุดแล้ว</option>
          </select>
        </label>
        <button class="clear-filters-button" type="button" @click="clearHistoryFilters">
          ล้างตัวกรอง
        </button>
      </form>
    </vx-card>
    <vx-card v-show="activeTab === 'HISTORY'">
      <div class="process-card-heading">
        <div>
          <span class="process-eyebrow">CalculationLog</span>
          <h3>ประวัติการคำนวณดอกเบี้ยรายวัน</h3>
          <div class="process-meta" aria-live="polite">
            {{ historyLoading ? "กำลังโหลด…" : "ทั้งหมด " + historyTotal + " รายการ" }}
          </div>
        </div>
        <button
          type="button"
          class="process-icon-button"
          title="รีเฟรชประวัติ"
          aria-label="รีเฟรชประวัติ"
          :disabled="historyLoading"
          @click="loadHistory(historyPage)"
        >
          <feather-icon icon="RotateCwIcon" />
        </button>
      </div>
      <p class="process-notice">
        แสดง CalculationLog สรุปรอบประเภท DailyCalculatorsRun · รวมประวัติรุ่นเก่าระหว่างรอย้ายข้อมูล
        และงานคำนวณตามวันอ้างอิง รวมทั้งรอบที่กำลังทำงานและรอบที่เสร็จแล้ว
      </p>
      <p v-if="historyError" role="alert" class="text-danger">{{ historyError }}</p>
      <div class="qr-table-wrap" :aria-busy="historyLoading ? 'true' : 'false'">
        <table class="qr-table">
          <thead>
            <tr>
              <th>Run ID</th>
              <th>เริ่ม–สิ้นสุด</th>
              <th>สถานะ</th>
              <th>แหล่งที่มา</th>
              <th>ทั้งหมด</th>
              <th>ประมวลผลแล้ว</th>
              <th>สำเร็จ</th>
              <th>ไม่สำเร็จ</th>
              <th>ระยะเวลา</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="run in historyItems" :key="run.id">
              <td><code class="process-run">{{ run.id }}</code></td>
              <td>
                {{ formatDate(run.startAt) }}
                <div class="process-meta">ถึง {{ run.endAt ? formatDate(run.endAt) : "ยังไม่สิ้นสุด" }}</div>
              </td>
              <td>
                <span class="process-badge" :class="dailyRunClass(run.status)">
                  {{ dailyRunStatus(run.status) }}
                </span>
              </td>
              <td>{{ calculationSource(run.source) }}</td>
              <td>{{ number(run.totalContracts) }}</td>
              <td>{{ number(run.processedContracts) }}</td>
              <td>{{ number(run.completedContracts) }}</td>
              <td>{{ number(run.failedContracts) }}</td>
              <td>{{ formatDuration(run.elapsedSeconds) }}</td>
            </tr>
            <tr v-if="!historyItems.length">
              <td colspan="9">{{ historyLoading ? "กำลังโหลด…" : historyError ? "ไม่สามารถโหลดประวัติได้" : "ไม่พบประวัติตามเงื่อนไข" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="qr-pagination">
        <label class="qr-page-size">
          รายการต่อหน้า:
          <select :value="historyPageSize" :disabled="historyLoading" @change="changeHistorySize($event.target.value)">
            <option v-for="size in [5, 10, 20, 25, 50, 100]" :key="size" :value="size">{{ size }}</option>
          </select>
        </label>
        <span>
          {{ historyItems.length ? (historyPage - 1) * historyPageSize + 1 : 0 }}–{{
            historyItems.length ? (historyPage - 1) * historyPageSize + historyItems.length : 0
          }} จาก {{ historyTotal }}
        </span>
        <nav class="qr-page-buttons" aria-label="แบ่งหน้าประวัติการคำนวณ">
          <button type="button" aria-label="หน้าแรก" :disabled="historyLoading || !!historyError || historyPage <= 1" @click="loadHistory(1)">«</button>
          <button type="button" aria-label="ก่อนหน้า" :disabled="historyLoading || !!historyError || historyPage <= 1" @click="loadHistory(historyPage - 1)">‹</button>
          <span>{{ historyPage }} / {{ historyPages }}</span>
          <button type="button" aria-label="ถัดไป" :disabled="historyLoading || !!historyError || historyPage >= historyPages" @click="loadHistory(historyPage + 1)">›</button>
          <button type="button" aria-label="หน้าสุดท้าย" :disabled="historyLoading || !!historyError || historyPage >= historyPages" @click="loadHistory(historyPages)">»</button>
        </nav>
      </div>
    </vx-card>
    <vx-card
      v-if="activeTab === 'CONTRACTS' && selected"
      class="mt-6"
      :title="'ตรวจการเปลี่ยนแปลง · ' + selected.contractNo"
    >
      <div class="qr-toolbar">
        <div>
          เทียบกับเวลาเริ่ม {{ formatDate(selected.startedAt) }}
          <div class="process-meta">
            รหัสรอบงาน {{ selected.runId || "ไม่มีรหัส" }}
          </div>
        </div>
        <button
          class="process-button"
          size="small"
          type="button"
          :disabled="busy"
          @click="inspect(selected)"
        >
          ตรวจอีกครั้ง
        </button>
      </div>
      <p class="process-notice">
        ตรวจเฉพาะข้อมูลที่ commit แล้วและยังมีอยู่ โดยเทียบ updatedAt
        หลังเวลาเริ่ม ไม่เห็นงานที่ยังไม่ commit หรือรายการที่ถูกลบ
        และระบุไม่ได้ว่าใครเป็นผู้เปลี่ยน
        ผลนี้ยังไม่ยืนยันว่าปลดสถานะหรือคำนวณซ้ำได้อย่างปลอดภัย
      </p>
      <p v-if="inspecting" aria-live="polite">กำลังตรวจ model ที่เกี่ยวข้อง…</p>
      <p v-if="inspectionError" role="alert" class="text-danger">
        {{ inspectionError }}
      </p>
      <template v-if="inspection">
        <p class="mb-4" role="status">
          {{ inspectionMessage(inspection.status) }} ·
          {{ formatDate(inspection.checkedAt) }}
        </p>
        <div v-if="inspection.models.length" class="qr-table-wrap">
          <table class="qr-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>ประเภทข้อมูล</th>
                <th>ผลตรวจหลังเริ่มงาน</th>
                <th>จำนวนรายการ</th>
                <th>อัปเดตล่าสุด (เวลาไทย)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in inspection.models" :key="item.model">
                <td>
                  <strong>{{ item.model }}</strong>
                </td>
                <td>{{ categoryName(item.category) }}</td>
                <td>
                  <span
                    class="process-badge"
                    :class="'change-' + item.status"
                    >{{ changeText(item.status) }}</span
                  >
                </td>
                <td>{{ item.count === null ? "—" : item.count }}</td>
                <td>{{ item.latestAt ? formatDate(item.latestAt) : "—" }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="process-meta mt-4">
          Loan.updatedAt อาจเปลี่ยนจากการจองงานเอง ส่วน model
          ผลคำนวณอาจเปลี่ยนตามปกติ ผลตรวจไม่ได้บ่งชี้ว่าเกิดข้อผิดพลาดเสมอไป
        </p>
      </template>
    </vx-card>
  </div>
</template>
<script>
import gql from "graphql-tag";
import { apolloClient } from "@/setup/apollo-setup";

const QUERY = gql`
  query CalculationProcesses(
    $page: Int!
    $pageSize: Int!
    $search: String!
    $type: String!
  ) {
    calculationProcesses(
      page: $page
      pageSize: $pageSize
      search: $search
      type: $type
    ) {
      total
      page
      pageSize
      checkedAt
      targetDate
      logReadAvailable
      summary {
        processingCount
        pendingDailyCount
        runningCount
        confirmingCount
        possiblyStuckCount
      }
      runtimeConfig {
        maxWaitMs
        timeoutMs
        staleGraceMs
        expectedRunMs
        staleAfterMs
        maxWaitSource
        timeoutSource
        staleGraceSource
      }
      items {
        process
        pendingDaily
        loanId
        contractId
        contractNo
        contractName
        runId
        startedAt
        runtimeStatus
        elapsedSeconds
        expectedEndAt
        staleAt
        method
        calculationType
        workCount
        logStartedAt
        logEndedAt
      }
    }
  }
`;
const DAILY_LATEST = gql`
  query DailyCalculationLatest {
    dailyCalculationLatest {
      id
      source
      date
      startAt
      endAt
      isRunning
      elapsedSeconds
      status
      stage
      errorCode
      totalContracts
      startedContracts
      processedContracts
      completedContracts
      failedContracts
      pendingContracts
      progressUpdatedAt
      current {
        contractId
        contractIndex
        totalDates
        completedDates
        currentDate
        outcome
        errorCode
      }
    }
  }
`;
const DAILY_RUNS = gql`
  query DailyCalculationRuns(
    $page: Int!
    $pageSize: Int!
    $search: String!
    $source: String!
    $state: String!
  ) {
    dailyCalculationRuns(
      page: $page
      pageSize: $pageSize
      search: $search
      source: $source
      state: $state
    ) {
      total
      page
      pageSize
      items {
        id
        source
        date
        startAt
        endAt
        isRunning
        elapsedSeconds
        status
        totalContracts
        processedContracts
        completedContracts
        failedContracts
      }
    }
  }
`;
const INSPECTION = gql`
  query CalculationProcessInspection(
    $loanId: ID!
    $runId: String
    $startedAt: String
  ) {
    calculationProcessInspection(
      loanId: $loanId
      runId: $runId
      startedAt: $startedAt
    ) {
      status
      checkedAt
      models {
        model
        category
        status
        count
        latestAt
      }
    }
  }
`;
const ACCESS = gql`
  query ReleaseAccess {
    administratorAccess(
      route: "/administartor/processMonitoring"
      action: "edit"
    )
  }
`;
const RELEASE = gql`
  mutation ReleaseCalculationProcesses($targets: [CalculationProcessTarget!]!) {
    releaseCalculationProcesses(targets: $targets) {
      stopped
      results {
        loanId
        status
        message
      }
    }
  }
`;
export default {
  data() {
    return {
      activeTab: "LATEST",
      search: "",
      searchTimer: null,
      latestRefreshTimer: null,
      disposed: false,
      latestRun: null,
      latestLoading: false,
      latestError: "",
      historySearch: "",
      historyAppliedSearch: "",
      historySearchTimer: null,
      historySource: "ALL",
      historyState: "ALL",
      historyRequestId: 0,
      historyLoading: false,
      historyError: "",
      historyItems: [],
      historyTotal: 0,
      historyPage: 1,
      historyPageSize: 10,
      canRelease: false,
      preparingRelease: false,
      releasing: false,
      releaseTargets: [],
      releaseResults: [],
      releaseError: "",
      inspecting: false,
      selected: null,
      inspection: null,
      inspectionError: "",
      appliedSearch: "",
      type: "ALL",
      appliedType: "ALL",
      items: [],
      total: 0,
      page: 1,
      pageSize: 25,
      checkedAt: null,
      targetDate: null,
      logReadAvailable: true,
      summary: {
        processingCount: 0,
        pendingDailyCount: 0,
        runningCount: 0,
        confirmingCount: 0,
        possiblyStuckCount: 0,
      },
      runtimeConfig: null,
      loading: false,
      error: "",
    };
  },
  computed: {
    busy() {
      return (
        this.loading ||
        this.inspecting ||
        this.preparingRelease ||
        this.releasing ||
        this.releaseTargets.length > 0
      );
    },
    pages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    historyPages() {
      return Math.max(1, Math.ceil(this.historyTotal / this.historyPageSize));
    },
    latestProgressPercent() {
      if (!this.latestRun || !this.latestRun.totalContracts) return 0;
      return Math.min(
        100,
        Math.floor(
          (this.latestRun.processedContracts / this.latestRun.totalContracts) * 100
        )
      );
    },
  },
  created() {
    this.load(1);
    this.loadLatest();
    apolloClient
      .query({ query: ACCESS, fetchPolicy: "no-cache" })
      .then((response) => {
        this.canRelease =
          !response.errors &&
          !!(response.data && response.data.administratorAccess);
      })
      .catch(() => {
        this.canRelease = false;
      });
  },
  beforeDestroy() {
    this.disposed = true;
    this.cancelSearch();
    this.cancelHistorySearch();
    this.historyRequestId++;
    clearTimeout(this.latestRefreshTimer);
    this.latestRefreshTimer = null;
  },
  methods: {
    cancelSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    },
    scheduleLatestPoll() {
      clearTimeout(this.latestRefreshTimer);
      if (this.disposed) return;
      const delay = this.latestRun && this.latestRun.isRunning ? 3000 : 30000;
      this.latestRefreshTimer = setTimeout(() => {
        this.latestRefreshTimer = null;
        if (document.hidden) this.scheduleLatestPoll();
        else this.loadLatest();
      }, delay);
    },
    async loadLatest() {
      if (this.latestLoading) return;
      clearTimeout(this.latestRefreshTimer);
      this.latestRefreshTimer = null;
      this.latestLoading = true;
      this.latestError = "";
      try {
        const response = await apolloClient.query({
          query: DAILY_LATEST,
          fetchPolicy: "no-cache",
        });
        if (response.errors || !response.data)
          throw new Error("load latest failed");
        this.latestRun = response.data.dailyCalculationLatest;
      } catch (error) {
        this.latestError =
          "โหลดสถานะการประมวลผลล่าสุดไม่สำเร็จ กรุณากดรีเฟรชอีกครั้ง";
      } finally {
        this.latestLoading = false;
        if (!this.disposed) this.scheduleLatestPoll();
      }
    },
    selectHistoryTab() {
      this.activeTab = "HISTORY";
      return this.loadHistory(this.historyPage);
    },
    cancelHistorySearch() {
      clearTimeout(this.historySearchTimer);
      this.historySearchTimer = null;
    },
    scheduleHistorySearch(event = {}) {
      this.cancelHistorySearch();
      if (event.isComposing) return;
      this.historySearchTimer = setTimeout(() => {
        this.historySearchTimer = null;
        this.applyHistorySearch();
      }, 300);
    },
    applyHistorySearch() {
      this.cancelHistorySearch();
      this.historyAppliedSearch = this.historySearch.trim();
      return this.loadHistory(1);
    },
    clearHistoryFilters() {
      this.cancelHistorySearch();
      this.historySearch = "";
      this.historyAppliedSearch = "";
      this.historySource = "ALL";
      this.historyState = "ALL";
      return this.loadHistory(1);
    },
    changeHistorySize(value) {
      this.historyPageSize = Number(value);
      return this.loadHistory(1);
    },
    async loadHistory(page) {
      const requestId = ++this.historyRequestId;
      this.historyLoading = true;
      this.historyError = "";
      try {
        const response = await apolloClient.query({
          query: DAILY_RUNS,
          variables: {
            page,
            pageSize: this.historyPageSize,
            search: this.historyAppliedSearch,
            source: this.historySource,
            state: this.historyState,
          },
          fetchPolicy: "no-cache",
        });
        if (this.disposed || requestId !== this.historyRequestId) return;
        if (
          response.errors ||
          !response.data ||
          !response.data.dailyCalculationRuns
        )
          throw new Error("load history failed");
        const result = response.data.dailyCalculationRuns;
        this.historyItems = result.items;
        this.historyTotal = result.total;
        this.historyPage = result.page;
        this.historyPageSize = result.pageSize;
      } catch (error) {
        if (this.disposed || requestId !== this.historyRequestId) return;
        this.historyItems = [];
        this.historyTotal = 0;
        this.historyError = "โหลดประวัติการคำนวณไม่สำเร็จ";
      } finally {
        if (requestId === this.historyRequestId) this.historyLoading = false;
      }
    },
    number(value) {
      return Number.isFinite(value) ? value.toLocaleString("th-TH") : "—";
    },
    latestRunHeading(run) {
      if (run.isRunning)
        return run.source === "RECONCILIATION"
          ? "กำลังคำนวณตามวันอ้างอิง"
          : "กำลังคำนวณดอกเบี้ยรายวัน";
      return run.endAt ? "ไม่มีการประมวลผลอยู่" : "รอบล่าสุดอาจค้าง";
    },
    calculationSource(value) {
      return {
        CRON: "งานอัตโนมัติ (Cron)",
        MANUAL: "เรียก Mutation โดยผู้ดูแล",
        RECONCILIATION: "คำนวณตามวันอ้างอิง (กระทบยอด)",
        LEGACY_UNKNOWN: "ไม่ทราบ (ข้อมูลก่อนปรับปรุง)",
      }[value] || "ไม่ทราบแหล่งที่มา";
    },
    dailyRunStatus(value) {
      return (
        {
          RUNNING: "กำลังทำงาน",
          COMPLETED: "สำเร็จ",
          COMPLETED_WITH_ERRORS: "เสร็จพร้อมข้อผิดพลาด",
          FAILED: "ล้มเหลว",
          STALE: "หยุดอัปเดต · อาจค้าง",
        }[value] || "ไม่ทราบสถานะ"
      );
    },
    dailyRunClass(value) {
      return {
        "process-badge-active": value === "RUNNING",
        "process-badge-confirming": value === "COMPLETED_WITH_ERRORS",
        "process-badge-success": value === "COMPLETED",
        "process-badge-stuck": ["FAILED", "STALE"].includes(value),
      };
    },
    scheduleSearch(event = {}) {
      this.cancelSearch();
      if (event.isComposing) return;
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        this.load(1);
      }, 300);
    },
    async prepareRelease(row) {
      if (this.busy || !this.canRelease || (row && !row.process)) return;
      this.cancelSearch();
      this.preparingRelease = true;
      this.releaseError = "";
      this.releaseResults = [];
      try {
        let targets = row ? [row] : [];
        if (!row) {
          let last = 1;
          for (let page = 1; page <= last; page++) {
            const response = await apolloClient.query({
              query: QUERY,
              variables: {
                page,
                pageSize: 100,
                search: "",
                type: "PROCESSING",
              },
              fetchPolicy: "no-cache",
            });
            if (
              response.errors ||
              !response.data ||
              !response.data.calculationProcesses
            )
              throw Error("load failed");
            const result = response.data.calculationProcesses;
            if (page === 1) last = Math.ceil(result.total / 100);
            targets.push(...result.items);
          }
        }
        this.releaseTargets = targets.filter(
          (item, index) =>
            item.process === true &&
            targets.findIndex((other) => other.loanId === item.loanId) === index
        );
        if (!this.releaseTargets.length)
          this.releaseError = "ไม่มีรายการให้ปลดสถานะ";
      } catch (error) {
        this.releaseError = "โหลดรายการยืนยันไม่สำเร็จ ยังไม่ได้ปลดสถานะ";
      } finally {
        this.preparingRelease = false;
      }
    },
    async confirmRelease() {
      if (this.releasing || !this.canRelease || !this.releaseTargets.length)
        return;
      const targets = this.releaseTargets.slice();
      this.releasing = true;
      this.releaseResults = [];
      this.releaseError = "";
      try {
        for (let offset = 0; offset < targets.length; offset += 25) {
          const batch = targets.slice(offset, offset + 25);
          const response = await apolloClient.mutate({
            mutation: RELEASE,
            variables: {
              targets: batch.map(({ loanId, runId, startedAt }) => ({
                loanId,
                runId,
                startedAt,
              })),
            },
          });
          if (
            response.errors ||
            !response.data ||
            !response.data.releaseCalculationProcesses
          )
            throw Error("unknown response");
          const result = response.data.releaseCalculationProcesses;
          this.releaseResults.push(
            ...result.results.map((item) => ({
              ...item,
              contractNo:
                (batch.find((row) => row.loanId === item.loanId) || {})
                  .contractNo || item.loanId,
            }))
          );
          if (result.stopped) {
            this.releaseError =
              "หยุดชุดงาน: ผลบางรายการไม่แน่นอน รายการที่ยังไม่แสดงผลไม่ได้ดำเนินการ ต้องตรวจข้อมูลก่อนทำซ้ำ";
            break;
          }
        }
      } catch (error) {
        this.releaseError =
          "ยืนยันผลคำสั่งล่าสุดไม่ได้ หยุดชุดงานแล้ว ต้องรีเฟรชและตรวจฐานข้อมูลก่อนทำซ้ำ";
      } finally {
        this.releasing = false;
        this.releaseTargets = [];
      }
      await this.refreshList();
    },
    clearFilters() {
      this.search = "";
      this.type = "ALL";
      return this.load(1, "", "ALL");
    },
    changeSize(value) {
      this.pageSize = Number(value);
      return this.load(1, this.appliedSearch, this.appliedType);
    },
    async inspect(row) {
      if (this.busy || !row.process) return;
      this.selected = row;
      this.inspection = null;
      this.inspectionError = "";
      this.inspecting = true;
      try {
        const response = await apolloClient.query({
          query: INSPECTION,
          variables: {
            loanId: row.loanId,
            runId: row.runId,
            startedAt: row.startedAt,
          },
          fetchPolicy: "no-cache",
        });
        if (
          response.errors ||
          !response.data ||
          !response.data.calculationProcessInspection
        )
          throw new Error("load failed");
        this.inspection = response.data.calculationProcessInspection;
      } catch (error) {
        this.inspectionError =
          "ตรวจสอบไม่สำเร็จ ยังสรุปสถานะไม่ได้ กรุณาลองใหม่";
      } finally {
        this.inspecting = false;
      }
    },
    inspectionMessage(status) {
      return (
        {
          STALE:
            "รอบงานเปลี่ยนหรือสิ้นสุดแล้ว กรุณารีเฟรชรายการก่อนตรวจอีกครั้ง",
          UNKNOWN_START:
            "ไม่มีเวลาเริ่มที่ใช้เปรียบเทียบได้ ยังสรุปการเปลี่ยนแปลงไม่ได้",
          PARTIAL: "ตรวจได้บางส่วน มี model ที่อ่านไม่สำเร็จ",
          CHECKED: "ตรวจข้อมูลที่อ่านได้แล้ว ดูผลราย model ด้านล่าง",
        }[status] || "ยังสรุปผลไม่ได้"
      );
    },
    categoryName(value) {
      return (
        {
          input: "ข้อมูลประกอบการคำนวณ",
          result: "ผลการคำนวณ",
          state: "สถานะรอบงาน",
        }[value] || value
      );
    },
    changeText(value) {
      return (
        {
          CHANGED: "พบการเปลี่ยนแปลง",
          NOT_FOUND: "ไม่พบในข้อมูลที่อ่านได้",
          UNAVAILABLE: "อ่านข้อมูลไม่ได้",
        }[value] || "ไม่ทราบ"
      );
    },
    runtimeText(value) {
      return (
        {
          RUNNING: "กำลังประมวลผล",
          CONFIRMING: "เกิน timeout · รอยืนยันผล",
          POSSIBLY_STUCK: "อาจค้าง · ต้องตรวจสอบ",
          UNKNOWN_START: "ไม่ทราบเวลาเริ่ม · ต้องตรวจสอบ",
          NOT_PROCESSING: "ไม่ได้ประมวลผล",
        }[value] || "ไม่ทราบสถานะ"
      );
    },
    runtimeClass(value) {
      return {
        "process-badge-active": value === "RUNNING",
        "process-badge-confirming": value === "CONFIRMING",
        "process-badge-stuck": ["POSSIBLY_STUCK", "UNKNOWN_START"].includes(value),
      };
    },
    formatMilliseconds(value) {
      if (!Number.isFinite(value)) return "—";
      const seconds = Math.floor(value / 1000);
      if (seconds < 60) return `${seconds} วินาที`;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return remainingSeconds
        ? `${minutes} นาที ${remainingSeconds} วินาที`
        : `${minutes} นาที`;
    },
    configSourceText(config) {
      const sources = [config.maxWaitSource, config.timeoutSource, config.staleGraceSource];
      return sources.every((source) => source === "DEFAULT")
        ? "ค่าเริ่มต้นทั้งหมด"
        : sources.every((source) => source === "ENV")
        ? "กำหนดจาก Environment ทั้งหมด"
        : "ใช้ Environment ร่วมกับค่าเริ่มต้น";
    },
    refreshList() {
      return this.load(this.page, this.appliedSearch, this.appliedType);
    },
    async load(page, search = this.search.trim(), type = this.type, preserveSelection = false) {
      this.cancelSearch();
      if (this.busy) return;
      if (!preserveSelection) {
        this.selected = null;
        this.inspection = null;
        this.inspectionError = "";
      }
      this.loading = true;
      this.error = "";
      try {
        const response = await apolloClient.query({
          query: QUERY,
          variables: { page, pageSize: this.pageSize, search, type },
          fetchPolicy: "no-cache",
        });
        if (
          response.errors ||
          !response.data ||
          !response.data.calculationProcesses
        )
          throw new Error("load failed");
        const result = response.data.calculationProcesses;
        this.items = result.items;
        this.total = result.total;
        this.page = result.page;
        this.pageSize = result.pageSize;
        this.checkedAt = result.checkedAt;
        this.targetDate = result.targetDate;
        this.logReadAvailable = result.logReadAvailable;
        this.summary = result.summary;
        this.runtimeConfig = result.runtimeConfig;
        if (preserveSelection && this.selected) {
          this.selected = this.items.find((item) => item.loanId === this.selected.loanId) || null;
          if (!this.selected) this.inspection = null;
        }
        this.appliedSearch = search;
        this.appliedType = type;
      } catch (error) {
        this.items = [];
        this.total = 0;
        this.checkedAt = null;
        this.targetDate = null;
        this.error =
          "โหลดรายการไม่สำเร็จ กรุณาตรวจสอบสิทธิ์หรือการเชื่อมต่อ แล้วกดรีเฟรช";
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      if (!value || !Number.isFinite(Date.parse(value))) return "ไม่ทราบเวลา";
      return new Date(value).toLocaleString("th-TH", {
        timeZone: "Asia/Bangkok",
      });
    },
    formatDuration(value) {
      if (!Number.isFinite(value))
        return "ไม่ทราบระยะเวลา";
      const seconds = Math.max(0, value);
      const days = Math.floor(seconds / 86400);
      const hours = Math.floor((seconds % 86400) / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = seconds % 60;
      if (days) return `${days} วัน ${hours} ชั่วโมง ${minutes} นาที`;
      if (hours) return `${hours} ชั่วโมง ${minutes} นาที ${remainingSeconds} วินาที`;
      if (minutes) return `${minutes} นาที ${remainingSeconds} วินาที`;
      return `${remainingSeconds} วินาที`;
    },
  },
};
</script>
<style scoped>
.process-badge {
  display: block;
  width: fit-content;
  padding: 4px 8px;
  margin-bottom: 5px;
  border-radius: 6px;
  background: #f1f3f5;
  color: #475569;
  font-size: 12px;
}
.process-badge-active {
  background: #e9e8ff;
  color: #4840a3;
}
.process-badge-pending {
  background: #fff3d6;
  color: #805300;
}
.process-badge-confirming {
  background: #fff3d6;
  color: #805300;
}
.process-badge-stuck {
  background: #fce8e8;
  color: #a52727;
}
.process-badge-success {
  background: #e7f7ed;
  color: #217a43;
}
.process-badge-idle {
  background: #f1f2f4;
  color: #6e6b7b;
}
.latest-run-state {
  text-align: right;
}
.latest-run-state .process-badge {
  margin-left: auto;
}
.process-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  border-bottom: 1px solid #dedce4;
  overflow-x: auto;
}
.process-tabs button {
  flex: 0 0 auto;
  padding: 13px 18px;
  border: 0;
  border-bottom: 3px solid transparent;
  background: transparent;
  color: #777383;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  white-space: nowrap;
}
.process-tabs button.active {
  border-bottom-color: #6555d0;
  color: #6555d0;
}
.process-tabs button:focus-visible {
  outline: 3px solid #ac9dff;
  outline-offset: 2px;
}
.process-card-heading,
.latest-run-title,
.latest-progress-label,
.latest-run-meta,
.current-progress > div:first-child {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.process-card-heading {
  margin-bottom: 20px;
}
.process-card-heading h3 {
  margin: 3px 0 0;
}
.process-eyebrow {
  color: #6555d0;
  font-size: 12px;
  font-weight: 600;
}
.process-icon-button {
  display: inline-flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border: 0;
  border-radius: 50%;
  background: #f4f2ff;
  color: #6555d0;
  cursor: pointer;
  font-size: 28px;
}
.process-icon-button:disabled {
  opacity: 0.5;
  cursor: default;
}
.process-icon-button .feather-icon {
  width: 18px;
  height: 18px;
}
.latest-run-id {
  margin-top: 6px;
  max-width: none;
}
.latest-overview {
  margin-top: 20px;
}
.latest-progress-label {
  margin-top: 22px;
  color: #4b465c;
}
.latest-run progress,
.current-progress progress {
  width: 100%;
  height: 10px;
  margin-top: 10px;
  border: 0;
  border-radius: 10px;
  overflow: hidden;
  accent-color: #6555d0;
}
.latest-run progress::-webkit-progress-bar,
.current-progress progress::-webkit-progress-bar {
  background: #eceaf5;
}
.latest-run progress::-webkit-progress-value,
.current-progress progress::-webkit-progress-value {
  background: #6555d0;
}
.current-progress {
  padding: 16px;
  margin-top: 20px;
  border-radius: 8px;
  background: #f8f7fc;
}
.current-progress code {
  overflow-wrap: anywhere;
}
.current-progress-detail {
  margin-top: 10px;
  color: #777383;
}
.latest-run-meta {
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-top: 16px;
  color: #777383;
  font-size: 12px;
}
.process-overview {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}
.process-stat {
  min-height: 124px;
  padding: 18px;
  border: 1px solid #ebe9ee;
  border-radius: 10px;
  background: #fff;
}
.process-stat span,
.process-stat small {
  display: block;
}
.process-stat strong {
  display: block;
  margin: 8px 0;
  font-size: 30px;
  line-height: 1;
}
.process-stat small {
  color: #777383;
}
.process-stat-running {
  border-left: 4px solid #6555d0;
}
.process-stat-success {
  border-left: 4px solid #3a8f78;
}
.process-stat-confirming {
  border-left: 4px solid #f1a62a;
}
.process-stat-stuck {
  border-left: 4px solid #b72d3b;
}
.process-stat-pending {
  border-left: 4px solid #3a8f78;
}
.process-config {
  margin-top: 18px;
  padding: 12px 16px;
  border-radius: 6px;
  background: #f7f6fb;
  line-height: 1.7;
}
.process-config-source {
  color: #777383;
}
.process-notice-warning {
  margin-top: 16px;
  border-left-color: #f1a62a;
  background: #fff8e8;
}

.qr-action-result {
  padding: 16px;
  border-radius: 6px;
  line-height: 1.7;
  overflow-wrap: anywhere;
}
.qr-action-success {
  background: #eaf7ef;
  color: #176534;
}
.qr-action-warning {
  background: #fff4df;
  color: #805600;
}
.qr-table-wrap {
  overflow-x: auto;
}
.qr-table {
  width: 100%;
  border-collapse: collapse;
}
.qr-table th,
.qr-table td {
  padding: 12px;
  border-bottom: 1px solid #ddd;
  text-align: left;
  white-space: nowrap;
}
.qr-actions > * {
  margin: 3px;
}
.qr-pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 24px;
  align-items: center;
  padding-top: 24px;
  margin-top: 24px;
  border-top: 1px solid #ebe9ee;
  color: #777383;
  font-size: 16px;
}
.qr-page-size,
.qr-page-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
}
.qr-page-size select {
  width: 110px;
  height: 42px;
  padding: 8px 20px;
  border: 1px solid #d8d6de;
  border-radius: 8px;
  background: white;
  color: inherit;
  font: inherit;
}
.qr-page-buttons button {
  width: 40px;
  height: 40px;
  padding: 8px;
  border: 0;
  border-radius: 50%;
  color: #8c8797;
  background: transparent;
  cursor: pointer;
}
.qr-page-buttons button:disabled {
  opacity: 0.5;
  cursor: default;
}
.qr-page-buttons button:not(:disabled):hover {
  background: #f4f2ff;
}
.qr-page-buttons svg {
  width: 24px;
  height: 24px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.qr-toolbar {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}
.qr-toolbar input {
  width: 360px;
  max-width: 100%;
  padding: 10px 12px;
  border: 1px solid #d8d6de;
  border-radius: 6px;
}
.qr-filters button {
  margin-left: auto;
}
.qr-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
}
.qr-filters label {
  flex: 1 1 220px;
}
.qr-filters input,
.qr-filters select {
  display: block;
  width: 100%;
  padding: 9px 12px;
  margin-top: 5px;
  border: 1px solid #d5dce3;
  border-radius: 5px;
}
.qr-filters .clear-filters-button {
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 8px 12px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  align-self: flex-end;
  white-space: nowrap;
}
.qr-filters button:disabled {
  opacity: 0.5;
  cursor: default;
}
.process-meta {
  color: #777383;
  font-size: 12px;
  margin-top: 5px;
  overflow-wrap: anywhere;
}
.process-name {
  max-width: 260px;
  white-space: normal;
  color: #6e6b7b;
  margin-top: 5px;
}
.process-run {
  display: block;
  max-width: 170px;
  white-space: normal;
  overflow-wrap: anywhere;
  font-size: 12px;
  background: #f4f3f7;
  padding: 6px 8px;
  border-radius: 5px;
}
.process-notice {
  background: #f5f3ff;
  border-left: 3px solid #7367f0;
  padding: 12px 16px;
  margin: 0 0 20px;
  line-height: 1.7;
}
.process-selected {
  background: #f8f7ff;
}
.process-badge {
  display: inline-block;
  border-radius: 5px;
  padding: 5px 9px;
  font-size: 12px;
  background: #f1f2f4;
}
.change-CHANGED {
  background: #fff1dc;
  color: #875400;
}
.change-UNAVAILABLE {
  background: #fce8e8;
  color: #a52727;
}
.qr-page-buttons button {
  font-size: 24px;
  padding: 0;
}
.system-process-page .process-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 8px 14px;
  margin: 3px;
  border: 1px solid #6555d0;
  border-radius: 6px;
  background: #6555d0;
  color: #fff;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}
.system-process-page .process-button.danger {
  border-color: #b72d3b;
  background: #fff;
  color: #a82533;
}
.system-process-page .process-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.system-process-page .process-button:focus-visible {
  outline: 3px solid #ac9dff;
  outline-offset: 2px;
}
.process-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}
.release-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  background: rgba(20, 20, 35, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.release-dialog {
  background: white;
  color: #333;
  padding: 24px;
  border-radius: 10px;
  width: 620px;
  max-width: 100%;
  max-height: 85vh;
  overflow: auto;
  line-height: 1.8;
}
.release-dialog ul {
  max-height: 220px;
  overflow: auto;
  margin: 16px 0;
  padding-left: 24px;
}
@media (max-width: 900px) {
  .process-overview {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 560px) {
  .process-overview {
    grid-template-columns: 1fr;
  }
}
</style>
